import React, { useState, useEffect } from 'react';
import { tenantApi, DashboardStats, TenantAlert } from '../services/api';
import StatsCard from '../components/StatsCard';

interface DashboardProps {
  selectedTenantId: string | null;
}

const Dashboard: React.FC<DashboardProps> = ({ selectedTenantId }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [alerts, setAlerts] = useState<TenantAlert[]>([]);
  const [acknowledgingAlertId, setAcknowledgingAlertId] = useState<number | null>(null);
  const [resolvingAlertId, setResolvingAlertId] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [eventTypeFilter, setEventTypeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedTenantId) {
      fetchStats();
    }
  }, [selectedTenantId]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsResponse, alertsResponse] = await Promise.all([
        tenantApi.getStats(selectedTenantId!),
        tenantApi.getAlerts(selectedTenantId!, 8),
      ]);
      setStats(statsResponse.stats);
      setAlerts(alertsResponse.alerts);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Gagal memuat statistik. Silakan coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    setAcknowledgingAlertId(alertId);
    try {
      const response = await tenantApi.acknowledgeAlert(alertId);
      setAlerts((current) =>
        current.map((alert) => (alert.id === alertId ? response.alert : alert))
      );
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    } finally {
      setAcknowledgingAlertId(null);
    }
  };

  const handleResolve = async (alertId: number) => {
    setResolvingAlertId(alertId);
    try {
      const response = await tenantApi.resolveAlert(alertId);
      setAlerts((current) =>
        current.map((alert) => (alert.id === alertId ? response.alert : alert))
      );
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    } finally {
      setResolvingAlertId(null);
    }
  };

  if (!selectedTenantId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Silakan pilih tenant untuk melihat statistik</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-red-900">Error</h3>
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Tidak ada data statistik tersedia</p>
      </div>
    );
  }

  const conversionRate = stats.totalOrders > 0 
    ? ((stats.paidOrders / stats.totalOrders) * 100).toFixed(1)
    : '0.0';

  const uniqueEventTypes = Array.from(new Set(alerts.map((alert) => alert.eventType))).sort();
  const pendingAlertCount = alerts.filter((alert) => alert.status === 'PENDING').length;
  const highSeverityAlertCount = alerts.filter(
    (alert) => alert.severity === 'HIGH' || alert.severity === 'CRITICAL'
  ).length;

  const filteredAlerts = alerts.filter((alert) => {
    const severityMatches = severityFilter === 'ALL' || alert.severity === severityFilter;
    const statusMatches = statusFilter === 'ALL' || alert.status === statusFilter;
    const eventTypeMatches = eventTypeFilter === 'ALL' || alert.eventType === eventTypeFilter;
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const searchMatches =
      normalizedSearch.length === 0 ||
      alert.title.toLowerCase().includes(normalizedSearch) ||
      alert.message.toLowerCase().includes(normalizedSearch) ||
      alert.eventType.toLowerCase().includes(normalizedSearch) ||
      alert.status.toLowerCase().includes(normalizedSearch);
    return severityMatches && statusMatches && eventTypeMatches && searchMatches;
  });

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard Statistik</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatsCard
          title="Total Orders"
          value={stats.totalOrders}
          color="blue"
        />
        
        <StatsCard
          title="Paid Orders"
          value={stats.paidOrders}
          color="green"
        />
        
        <StatsCard
          title="Pending Orders"
          value={stats.pendingOrders}
          color="yellow"
        />
        
        <StatsCard
          title="Failed Orders"
          value={stats.failedOrders}
          color="red"
        />
        
        <StatsCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          subtitle={`${stats.paidOrders}/${stats.totalOrders} orders`}
          color="purple"
        />
        
        <StatsCard
          title="Total Revenue"
          value={`Rp ${stats.totalRevenue.toLocaleString('id-ID')}`}
          color="indigo"
        />

        <StatsCard
          title="Pending Alerts"
          value={pendingAlertCount}
          subtitle={highSeverityAlertCount > 0 ? `${highSeverityAlertCount} high severity` : 'No high severity alerts'}
          color={highSeverityAlertCount > 0 ? 'red' : 'yellow'}
        />
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold">Ops Alerts</h3>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
              {filteredAlerts.length} shown
            </span>
            {pendingAlertCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                {pendingAlertCount} pending
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">Alert operasional terbaru untuk tenant yang dipilih.</p>
          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <label className="flex flex-col gap-1 text-sm text-gray-600 md:min-w-64">
              Search
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Cari title, message, event type, status..."
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Severity
              <select
                value={severityFilter}
                onChange={(event) => setSeverityFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="WARN">Warn</option>
                <option value="INFO">Info</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Event Type
              <select
                value={eventTypeFilter}
                onChange={(event) => setEventTypeFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All event types</option>
                {uniqueEventTypes.map((eventType) => (
                  <option key={eventType} value={eventType}>
                    {eventType}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Status
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All statuses</option>
                <option value="PENDING">Pending</option>
                <option value="ACKNOWLEDGED">Acknowledged</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </label>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredAlerts.length === 0 ? (
            <div className="px-6 py-5 text-sm text-gray-500">Belum ada alert operasional.</div>
          ) : (
            filteredAlerts.map((alert) => (
              <div key={alert.id} className="px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium text-gray-900">{alert.title}</div>
                    <div className="text-sm text-gray-600">{alert.message}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {alert.eventType} · {new Date(alert.createdAt).toLocaleString('id-ID')} · {alert.status}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      alert.severity === 'HIGH' || alert.severity === 'CRITICAL'
                        ? 'bg-red-100 text-red-700'
                        : alert.severity === 'WARN'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                    }`}>
                      {alert.severity}
                    </span>
                    <div className="flex items-center gap-2">
                      {alert.status === 'PENDING' && (
                        <button
                          type="button"
                          onClick={() => handleAcknowledge(alert.id)}
                          disabled={acknowledgingAlertId === alert.id}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {acknowledgingAlertId === alert.id ? 'Saving...' : 'Acknowledge'}
                        </button>
                      )}
                      {alert.status !== 'RESOLVED' && (
                        <button
                          type="button"
                          onClick={() => handleResolve(alert.id)}
                          disabled={resolvingAlertId === alert.id}
                          className="rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {resolvingAlertId === alert.id ? 'Saving...' : 'Resolve'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
